// src/pages/real-time-notification-communication-hub/components/DeliveryMonitor.jsx
import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import { notificationService } from '../../../services/notificationService';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const DeliveryMonitor = () => {
  const { user, isAdmin } = useAuth();

  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [err, setErr] = useState('');

  // ✅ Si quieres otra forma de filtrar, cambia este array:
  // - system_alert / campaign_update / task_reminder / etc.
  // - y si en metadata guardas channel/status/recipient, lo leerá.
  const MONITOR_TYPES = useMemo(
    () => ['delivery_update', 'message_delivery', 'whatsapp_delivery', 'email_delivery', 'sms_delivery', 'system_alert'],
    []
  );

  const getChannelIcon = (channel) => {
    const iconMap = {
      whatsapp: 'MessageCircle',
      email: 'Mail',
      sms: 'MessageSquare',
      in_app: 'Bell',
    };
    return iconMap?.[channel] || 'Send';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      delivered: 'text-green-600 bg-green-500/10',
      sent: 'text-green-600 bg-green-500/10',
      pending: 'text-yellow-600 bg-yellow-500/10',
      queued: 'text-yellow-600 bg-yellow-500/10',
      failed: 'text-red-600 bg-red-500/10',
      error: 'text-red-600 bg-red-500/10',
    };
    return colorMap?.[String(status || '').toLowerCase()] || 'text-gray-600 bg-gray-500/10';
  };

  const normalizeStatus = (n) => {
    const s = String(n || '').toLowerCase();
    if (['delivered', 'sent'].includes(s)) return 'delivered';
    if (['pending', 'queued'].includes(s)) return 'pending';
    if (['failed', 'error'].includes(s)) return 'failed';
    return 'pending';
  };

  const loadMonitor = async () => {
    setErr('');
    setLoading(true);
    try {
      // ✅ Admin: trae todo. Usuario: trae suyo (aunque este monitor solo se muestra para admin en tu UI)
      const rows = await notificationService.getNotifications(user?.id, {
        limit: 200,
        isAdmin: !!isAdmin,
      });

      // ✅ Convertimos notificaciones en “deliveries”
      const deliveries = (rows || [])
        .filter((n) => {
          const t = String(n?.type || '');
          // Si no tienes tipos dedicados, igual se mostrará system_alert (útil para debug)
          return MONITOR_TYPES.includes(t) || (n?.metadata && (n?.metadata?.delivery || n?.metadata?.channel));
        })
        .map((n) => {
          const md = n?.metadata || {};

          // Intentamos leer "channel/status/recipient/template" desde metadata.
          // Si no existen, hacemos fallback con title/message.
          const channel =
            md?.channel ||
            md?.delivery?.channel ||
            (String(n?.type || '').includes('whatsapp') ? 'whatsapp' : null) ||
            (String(n?.type || '').includes('email') ? 'email' : null) ||
            (String(n?.type || '').includes('sms') ? 'sms' : null) ||
            'in_app';

          const statusRaw = md?.status || md?.delivery?.status || md?.delivery_status || 'pending';
          const status = normalizeStatus(statusRaw);

          const recipient =
            md?.recipient ||
            md?.to ||
            md?.phone ||
            md?.email ||
            md?.delivery?.recipient ||
            md?.delivery?.to ||
            '—';

          const template =
            md?.templateName ||
            md?.template ||
            md?.message_template ||
            n?.title ||
            'Entrega';

          const createdAt = n?.createdAt || n?.created_at;

          return {
            id: n?.id,
            channel,
            recipient,
            template,
            status,
            createdAt,
            raw: n,
          };
        });

      setItems(deliveries);
    } catch (e) {
      console.error('Error loading delivery monitor:', e);
      setErr(e?.message || 'Error cargando el monitor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user?.id) return;
    loadMonitor();

    // ✅ Realtime: Admin escucha todo, usuario escucha lo suyo
    const sub = notificationService.subscribeToNotifications(
      user?.id,
      (payload) => {
        // Simple: recargar en INSERT/UPDATE
        if (payload?.eventType === 'INSERT' || payload?.eventType === 'UPDATE') {
          loadMonitor();
        }
      },
      { isAdmin: !!isAdmin }
    );

    return () => {
      notificationService.unsubscribe();
      // (sub se limpia dentro de unsubscribe, pero lo dejamos claro)
      void sub;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isAdmin]);

  const stats = useMemo(() => {
    const total = items.length;
    const delivered = items.filter((i) => i.status === 'delivered').length;
    const failed = items.filter((i) => i.status === 'failed').length;
    const pending = total - delivered - failed;

    const rate = total > 0 ? ((delivered / total) * 100).toFixed(1) : '0.0';

    return { total, delivered, pending, failed, rate };
  }, [items]);

  if (!isAdmin) {
    return (
      <div className="bg-card rounded-xl p-6 border border-border">
        <p className="text-sm text-muted-foreground">Este monitor solo está disponible para administradores.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Icon name="Activity" size={22} className="mr-2 text-primary" />
          <h2 className="text-lg font-semibold text-foreground">Delivery Monitor</h2>
        </div>
        <Button variant="outline" size="sm" iconName="RefreshCcw" onClick={loadMonitor} disabled={loading}>
          {loading ? 'Cargando...' : 'Refrescar'}
        </Button>
      </div>

      {err ? (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm font-medium">
          {err}
        </div>
      ) : null}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Send" size={20} className="text-primary" />
            <span className="text-xs text-muted-foreground">Total</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{stats.total}</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="CheckCircle" size={20} className="text-green-600" />
            <span className="text-xs text-muted-foreground">Delivered</span>
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.delivered}</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="Clock" size={20} className="text-yellow-600" />
            <span className="text-xs text-muted-foreground">Pending</span>
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
        </div>

        <div className="bg-card rounded-xl p-4 border border-border">
          <div className="flex items-center justify-between mb-2">
            <Icon name="XCircle" size={20} className="text-red-600" />
            <span className="text-xs text-muted-foreground">Failed</span>
          </div>
          <p className="text-2xl font-bold text-red-600">{stats.failed}</p>
        </div>
      </div>

      {/* Delivery Rate */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Delivery Rate</h3>
          <span className="text-2xl font-bold text-primary">{stats.rate}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div
            className="h-3 rounded-full bg-primary transition-all"
            style={{ width: `${stats.rate}%` }}
          />
        </div>
      </div>

      {/* Recent Deliveries */}
      <div className="bg-card rounded-xl p-6 border border-border">
        <div className="flex items-center mb-6">
          <Icon name="List" size={22} className="mr-3 text-primary" />
          <h2 className="text-xl font-semibold text-foreground">Recent Deliveries</h2>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <Icon name="Loader2" size={26} className="animate-spin text-primary mr-2" />
            <span className="text-sm text-muted-foreground">Cargando entregas...</span>
          </div>
        ) : items?.length === 0 ? (
          <div className="text-center py-10">
            <Icon name="Activity" size={44} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Aún no hay entregas registradas</p>
            <p className="text-xs text-muted-foreground mt-2">
              Tip: Para que esto sea “real”, guarda channel/status/recipient en notifications.metadata
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {items.slice(0, 25).map((delivery) => (
              <div
                key={delivery?.id}
                className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border"
                title={delivery?.raw?.message || ''}
              >
                <div className="flex items-center flex-1 min-w-0">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mr-3 flex-shrink-0">
                    <Icon name={getChannelIcon(delivery?.channel)} size={20} className="text-primary" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {delivery?.template}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {delivery?.recipient} •{' '}
                      {delivery?.createdAt
                        ? formatDistanceToNow(new Date(delivery.createdAt), { addSuffix: true, locale: es })
                        : '—'}
                    </p>
                  </div>
                </div>

                <div className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${getStatusColor(delivery?.status)}`}>
                  {delivery?.status}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeliveryMonitor;
